import { Anchor, Box, BoxExtendedProps, Button, Footer, Heading, Image, List, Main, Paragraph, RangeInput, ResponsiveContext, Select, Text, TextInput, ThemeContext, ThemeType, Tip, ToggleGroup } from "grommet"
import Plot from 'react-plotly.js'
import { useCallback, useContext, useEffect, useState } from "react"
import Icon from "./components/Icon"
import Loader from "./components/Loader"
import { Data, Layout } from "plotly.js"
import { useDarkTheme } from "./contexts/darkThemeContext"

function ValueSlider(
    { label, value, setValue, range, resetValue, hint, step = 0.1 }:
    { label: string, value: number, setValue: (v: number) => void, range: [number, number], resetValue: number, hint: string, step?: number }
) {
    const [tmpValue, setTmpValue] = useState<string>(value.toString())
    const update = (value: number) => {
        setValue(value)
        setTmpValue(value.toString())
    }

    return <Box pad='medium' background='background-front' gap='small' round='small'>
        <Box direction='row' align='stretch' gap='small'>
            <Box flex='grow' direction='row' align='center' gap='xsmall'>
                <Text>{label}</Text>
                <Info content={hint} />
            </Box>
            <Box width='4em' justify='end'>
                <TextInput
                    value={tmpValue}
                    onChange={e => setTmpValue(e.target.value)}
                    onBlur={e => update(
                        Math.max(
                            Math.min(
                                parseFloat(e.target.value),
                                range[1]
                            ),
                            range[0]
                        )
                    )}
                />
            </Box>
            {
                value !== resetValue
                ? <Tip content='Reset to default value'>
                    <Button
                        onClick={() => update(resetValue)}
                        icon={<Icon icon='undo' />}
                        pad='none'
                    />
                </Tip>
                : <Box width='1em' />
            }
        </Box>
        <RangeInput
            value={value > 0 ? value : 1e-6} // Visual bug when value = 0
            onChange={(e) => update(parseFloat(e.target.value))}
            min={range[0]}
            max={range[1]}
            step={step}
        />
    </Box>
}

function ValueResult({ label, hint, value }: { label: string, hint: string, value: string | undefined }) {
    return <Box align='center' background='brand' pad='medium'>
        <Box>
            {value
                ? <Text size='large' color='background-back' weight='bold'>{value}</Text>
                : <Loader />
            }
        </Box>
        <Box direction='row' align='center' gap='xsmall'>
            <Text size='medium' color='background-back'>{label}</Text>
            <Info content={hint} color='background-front' />
        </Box>
    </Box>
}

function Section({ title, children, ...props }: { title: string, children: JSX.Element | JSX.Element[] } & BoxExtendedProps) {
    return <Box style={{ minHeight: 'auto' }} background='background-contrast' pad='large' {...props}>
        <Heading level='2' margin={{top: '0', left: '0', right: '0', bottom: 'small'}}>{title}</Heading>
        {children}
    </Box>
}

function Info({ content, color = 'control' }: {content: string, color?: string}) {
    return <Tip content={content}>
        <Box>
            <Icon icon='info-circle' color={color} />
        </Box>
    </Tip>
}

function ChartLoader() {
    return <Box width='700px' height='450px' align='center' justify='center'>
        <Loader />
    </Box>
}

function Error() {
    const color = '#151515'
    return <Box direction='row' align='center' gap='small' background='warning' round='small' pad='small'>
        <Icon icon='warning' color={color} size='3x' />
        <Box>
            <Text color={color} size='large'>An error occurred</Text>
            <Text color={color} weight='bold'>Check your input parameters</Text>
        </Box>
    </Box>
}

function ChartError() {
    return <Box width='700px' height='450px' align='center' justify='center'>
        <Error />
    </Box>
}

function ChartContainer({ children }: { children: JSX.Element | JSX.Element[] }) {
    return <Box align="center" justify="center" background='background-back' round='small' style={{ overflow: 'hidden' }}>
        {children}
    </Box>
}

function round(v: number, n: number) {
    return new Intl.NumberFormat('en-IN', { maximumFractionDigits: n }).format(v)
}

function formatEnergy(value: number): string {
    // Server will give us values in kWh
    return `${round(value / 1e6, 2)} GWh`
}

function weibullPdf(k: number, x: number): number {
    return k * Math.pow(x, k - 1) * Math.exp(-Math.pow(x, k))
}

function plotTheme(theme: ThemeType, size: string): Partial<Layout> {
    return {
        width: size === 'small' ? 400 : undefined,
        height: size === 'small' ? 250 : undefined,
        paper_bgcolor: theme.global!.colors!["background-back"]!.toString(),
        plot_bgcolor: 'transparent',
        font: {
            family: 'Urbanist',
            color: theme.global!.colors!.text!.toString()
        }
    }
}

function WindSpeedChart({ amws, weibullShape }: { amws: number, weibullShape: number }) {
    const res = 100
    const min = 0
    const max = 2.5 * amws

    const xs = new Array(res)
    const ys = new Array(res)
    for (let i = 0; i <= res; i++) {
        xs[i] = min + (max - min) / res * i
        ys[i] = weibullPdf(weibullShape, xs[i] / amws)
    }

    const theme = useContext<ThemeType>(ThemeContext)
    const size = useContext(ResponsiveContext)

    return <Plot
        data={[
            {
                name: 'Probability density',
                type: 'scatter',
                mode: 'lines',
                x: xs,
                y: ys,
                line: { color: theme.global!.colors!.focus!.toString() }
            },
            {
                name: 'AMWS',
                type: 'scatter',
                mode: 'markers',
                marker: { color: theme.global!.colors!.brand!.toString() },
                x: [amws],
                y: [weibullPdf(weibullShape, 1.0)],
            }
        ]}
        layout={{
            xaxis: { title: 'Wind Speed [m/s]' },
            yaxis: { title: 'Probability Density [-]' },
            ...plotTheme(theme, size),
        }}
    />
}

function ContourChart({ quantity, data }: { quantity: string, data: ContourResults }) {
    const theme = useContext<ThemeType>(ThemeContext)
    const size = useContext(ResponsiveContext)
    const sizes = {
        'Power': 0.1,
        'Thrust': 0.1,
        'Torque': 0.01,
    } as {[quantity: string]: number}
    return <Plot
        data={[
            {
                name: quantity,
                type: 'contour',
                x: data.contours.x,
                y: data.contours.y,
                z: data.contours.z,
                autocontour: false,
                contours: {
                    start: 0.0,
                    end: Math.max(...data.contours.z.flat()),
                    size: sizes[quantity],
                    showlabels: true,
                    labelfont: { color: 'white' },
                    coloring: "heatmap",
                },
                colorscale: 'Viridis',
                colorbar: { title: quantity }
            },
            {
                name: 'Control path',
                type: 'scatter',
                mode: 'lines',
                x: data.controlPath.x,
                y: data.controlPath.y,
            }
        ]}
        layout={{
            xaxis: { title: 'Pitch angle [deg]' },
            yaxis: { title: 'TSR [-]' },
            ...plotTheme(theme, size)
        }}
    />
}

function TorqueSpeedChart({ data }: { data: TorqueSpeedResults}) {
    const theme = useContext<ThemeType>(ThemeContext)
    const size = useContext(ResponsiveContext)
    const d = data.modifiedPlot ?? data.baseLinePlot
    const vs = d.velocityCurves.map(v => {
        return {
            name: `${v.windspeed} m/s`,
            type: 'scatter',
            mode: 'lines',
            line: { dash: 'dot', width: 0.75 },
            x: v.omega,
            y: v.torque,
        } as Data
    })

    return <Plot
        data={[
            ...vs,
            (
                data.modifiedPlot != null
                ? {
                    name: 'Baseline control path',
                    type: 'scatter',
                    mode: 'lines',
                    line: { color: theme.global!.colors!.text!.toString(), dash: 'dot' },
                    x: data.baseLinePlot.controlCurve.rotorSpeed,
                    y: data.baseLinePlot.controlCurve.torque,
                }
                : {}
            ),
            {
                name: 'Control path',
                type: 'scatter',
                mode: 'lines',
                line: { color: theme.global!.colors!.text!.toString() },
                x: d.controlCurve.rotorSpeed,
                y: d.controlCurve.torque,
            },
        ]}
        layout={{
            xaxis: { title: 'Rotor speed [rad/s]'},
            yaxis: { title: 'Torque [Nm]' },
            ...plotTheme(theme, size)
        }}
    />
}

function PowerCurveChart({ data }: { data: PowerCurveResults}) {
    const theme = useContext<ThemeType>(ThemeContext)
    const size = useContext(ResponsiveContext)
    return <Plot
        data={[
            {
                name: 'Base power',
                type: 'scatter',
                mode: 'lines',
                y: data.basePower,
            },
            {
                name: 'Recalculated (raw)',
                type: 'scatter',
                mode: 'lines',
                y: data.powerSeriesRaw,
            },
            {
                name: 'Recalculated (tuned)',
                type: 'scatter',
                mode: 'lines',
                y: data.powerSeriesTuned,
            },
        ]}
        layout={{
            xaxis: {},
            yaxis: { title: 'Power [kW]' },
            ...plotTheme(theme, size)
        }}
    />
}

const url = 'https://fs5vbpv6asdgeceripbmrvycia0karlj.lambda-url.eu-west-1.on.aws'

type RequestContour = {
    path: 'contour-plot'
    coefficient: 'Power' | 'Thrust' | 'Torque'
}

type PerformanceRequest = {
    path: 'power-curve' | 'torque-speed'
    ratedSpeedFactor: number
    finePitchOffset: number
}

type RequestDataAep = {
    path: 'aep-calculation'
    ratedSpeedFactor: number
    finePitchOffset: number
    modeGainFactor: number
    amws: number
    weibullShape: number
}

type RequestData = RequestContour | PerformanceRequest | RequestDataAep

const defaults = {
    ratedSpeedFactor: 1.0,
    finePitchOffset: 0.0,
    modeGainFactor: 1.0,
} as any

async function requestData(
    request: RequestData
): Promise<Results | Error> {
    const path = request.path
    const data = {...request, path: undefined} as any

    // Remove parameters if they have the default value
    // This might speed up server response by avoiding unneeded computation
    Object.keys(data).forEach(key => {
        if(data[key] === defaults[key]) {
            data[key] = undefined
        }
    })

    return fetch(`${url}/${path}`, {
        method: "POST",
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
    }).then(r => r.json())
    .catch(reason => { return Error() })
}

type ContourResults = {
    contours: { x: number[], y: number[], z: number[] },
    controlPath: { x: number[], y: number[] },
}

type PowerCurveResults = {
    basePower: number[]
    powerSeriesRaw: number[]
    powerSeriesTuned: number[]
}

type TorqueSpeedPlot = {
    controlCurve: {
        rotorSpeed: number[]
        torque: number[]
    },
    velocityCurves: {
        omega: number[]
        torque: number[]
        windspeed: number
    }[]
}

type TorqueSpeedResults = {
    baseLinePlot: TorqueSpeedPlot
    modifiedPlot: TorqueSpeedPlot
}

type AepResults = {
    newAep: number | undefined
    baseAep: number
    relativeChange: number | undefined
}

type Results = ContourResults | PowerCurveResults | TorqueSpeedResults | AepResults

const themeOptions = [
    { label: 'Browser Default', value: 'auto' },
    { label: 'Light', value: 'light' },
    { label: 'Dark', value: 'dark' },
    { label: 'Classic', value: 'classic' },
    { label: 'High Contrast', value: 'highContrast' },
]

export default function AePeep() {
    const [ratedSpeedFactor, setRatedSpeedFactor] = useState<number>(defaults.ratedSpeedFactor)
    const [finePitchOffset, setFinePitchOffset] = useState<number>(defaults.finePitchOffset)
    const [modeGainFactor, setModeGainFactor] = useState<number>(defaults.modeGainFactor)
    const [amws, setAmws] = useState<number>(8.0)
    const [weibullShape, setWeibullShape] = useState<number>(2.0)

    const [contourChartType, setContourChartType] = useState<'Power' | 'Thrust' | 'Torque'>('Power')
    const [contourChart, setContourChart] = useState<ContourResults | Error | undefined>()
    const [torqueSpeedChart, setTorqueSpeedChart] = useState<TorqueSpeedResults | Error | undefined>()
    const [aep, setAep] = useState<AepResults | Error | undefined>()

    const [fetchedParams, setFetchedParams] = useState<number[] | undefined>()

    const getParams = useCallback(
        () => [ratedSpeedFactor, finePitchOffset, modeGainFactor, amws, weibullShape],
        [ratedSpeedFactor, finePitchOffset, modeGainFactor, amws, weibullShape]
    )

    // Contour Chart
    useEffect(() => {
        const fetchData = async () => {
            const data = await requestData({
                path: 'contour-plot',
                coefficient: contourChartType
            }) as ContourResults | Error
            setContourChart(data)
        }

        setContourChart(undefined)
        fetchData()
    }, [contourChartType])

    const updateAep = useCallback(() => {
        const fetchData = async () => {
            const data = await requestData({
                path: 'aep-calculation',
                ratedSpeedFactor: ratedSpeedFactor,
                finePitchOffset: finePitchOffset,
                modeGainFactor: modeGainFactor,
                amws: amws,
                weibullShape: weibullShape,
            }) as AepResults | Error
            if('newAep' in data && data.newAep) {
                data.relativeChange = (data.newAep - data.baseAep) / data.baseAep
            }
            setAep(data as AepResults)
        }

        setAep(undefined)
        fetchData()
    }, [ratedSpeedFactor, finePitchOffset, modeGainFactor, amws, weibullShape])

    const updateTorqueSpeed = useCallback(() => {
            const fetchData = async () => {
                const data = await requestData({
                    path: 'torque-speed',
                    ratedSpeedFactor: ratedSpeedFactor,
                    finePitchOffset: finePitchOffset,
                }) as TorqueSpeedResults | Error
                setTorqueSpeedChart(data)
            }

            setTorqueSpeedChart(undefined)
            fetchData()
    }, [ratedSpeedFactor, finePitchOffset ])

    const update = useCallback(() => {
        setFetchedParams(getParams())
        updateAep()
        setTorqueSpeedChart(undefined)
        updateTorqueSpeed()
    }, [getParams, updateAep, updateTorqueSpeed])

    const [theme, setTheme] = useState(localStorage.getItem('theme'))
    useEffect(() => {
        window.addEventListener('themeChanged', () => {
            setTheme(localStorage.getItem('theme'))
        })
    }, [])

    const darkTheme = useDarkTheme()
    const size = useContext(ResponsiveContext)
    const responsiveDir = size === 'small' ? 'column' : 'row'

    return <Main background='background-back' align='center' pad='medium'>
        <Box width={{ width: '100%',  max: '1500px' }} align='stretch' gap='medium'>
            <Box direction={responsiveDir} gap='medium' margin={{ horizontal: 'small' }} align='center' justify='between' style={{ minHeight: 'auto' }}>
                <Anchor href='https://bitbloom.tech/' target='_blank'>
                    <Image src={darkTheme ? '/AEPeep-White.svg' : '/AEPeep-Black.svg'} height='128px' />
                </Anchor>
                <Box direction='row' align='center' gap='small'>
                    <Text>Theme</Text>
                    <Select
                        valueKey='label'
                        options={themeOptions}
                        value={
                            themeOptions.find(
                                o => o.value === theme
                            ) ?? themeOptions[0]
                        }
                        onChange={({ option }) => {
                            localStorage.setItem(
                                'theme',
                                option.value
                            )
                            window.dispatchEvent(
                                new CustomEvent('themeChanged')
                            )
                        }}
                    />
                </Box>
            </Box>
            <Section title='Model'>
                <Box direction={responsiveDir} gap='small' justify='between'>
                    <Box>
                        <Paragraph>
                            AEPeep is a tool developed by <Anchor href='https://bitbloom.tech/' target='_blank'>Bitbloom</Anchor> which estimates the annual energy production (AEP) impacts of changes in wind turbine control.
                        </Paragraph>
                        <Paragraph>
                            The calculation uses power coefficient data (visible {responsiveDir === 'row' ? 'on the right' : 'below'}) from the reference <Anchor href='https://github.com/IEAWindTask37/IEA-3.4-130-RWT' target='_blank'>IEA task 37 reference 3.4MW turbine</Anchor> as a generic model.
                            With a defined wind speed distribution, it calculates an estimated AEP based on the baseline control path and an alternative one based on a control path modified by parameters in the "Turbine Control Changes" section.
                            This results in an estimated relative change in AEP.
                        </Paragraph>
                        <Paragraph>
                            Note that this is an estimate only and an accurate AEP change depends on the turbine type and other site conditions not modelled here.
                        </Paragraph>
                    </Box>
                    <ChartContainer>
                        { contourChart !== undefined
                            ? (
                                ('contours' in contourChart)
                                ? <ContourChart quantity={contourChartType} data={contourChart} />
                                : <ChartError/>
                            )
                            : <ChartLoader /> }
                        <ToggleGroup
                            alignSelf="center"
                            value={contourChartType}
                            onToggle={e => setContourChartType(e.value as 'Power' | 'Thrust' | 'Torque')}
                            options={[
                                {label: 'Power', value: 'Power'},
                                {label: 'Thrust', value: 'Thrust'},
                                {label: 'Torque', value: 'Torque'},
                            ]}
                            margin={{ bottom: 'small' }}
                        />
                    </ChartContainer>
                </Box>
            </Section>
            <Box direction={responsiveDir} height={{ min: 'auto' }} gap='medium' justify='stretch'>
                <Section title='Turbine Control Changes' flex='grow' align='start'>
                    <Box gap='small'>
                        <ValueSlider
                            label='Rated speed factor'
                            value={ratedSpeedFactor}
                            setValue={setRatedSpeedFactor}
                            range={[0.5, 1]}
                            step={0.01}
                            resetValue={defaults.ratedSpeedFactor}
                            hint='Change in rated rotor speed relative to the baseline control. The rated generator torque is changed accordingly such that the rated power point remains unchanged.'
                        />
                        <ValueSlider
                            label='Fine pitch offset [deg]'
                            value={finePitchOffset}
                            setValue={setFinePitchOffset}
                            range={[0, 20]}
                            resetValue={defaults.finePitchOffset}
                            hint='Change in pitch angle in partial load'
                        />
                        <ValueSlider
                            label='Mode gain factor'
                            value={modeGainFactor}
                            setValue={setModeGainFactor}
                            range={[0.1, 1.1]}
                            step={0.01}
                            resetValue={defaults.modeGainFactor}
                            hint='Mode gain is the ratio of generator torque to the square of generator speed in partial load. This factor moves the turbine away from the optimal tip-speed ratio.'
                        />
                    </Box>
                </Section>
                <Section title='Wind Conditions' flex='grow'>
                    <Box gap='small' align='start'>
                        <Box gap='small' direction={responsiveDir}>
                            <ValueSlider
                                label='Annual mean wind speed [m/s]'
                                value={amws}
                                setValue={setAmws}
                                range={[1.0, 20.0]}
                                resetValue={8.0}
                                hint='The annual mean wind speed (AMWS)'
                            />
                            <ValueSlider
                                label='Weibull shape parameter'
                                value={weibullShape}
                                setValue={setWeibullShape}
                                range={[0.1, 10.0]}
                                resetValue={2.0}
                                hint='Shape parameter (often labelled k) for the Weibull distribution'
                            />
                        </Box>
                        <ChartContainer>
                            <WindSpeedChart amws={amws} weibullShape={weibullShape} />
                        </ChartContainer>
                    </Box>
                </Section>
            </Box>
            <Box align="center" height={{ min: 'auto' }}>
                <Button
                    primary
                    size='xxlarge'
                    pad='medium'
                    style={{ borderRadius: '10em' }}
                    label={fetchedParams ? 'Recalculate AEP' : 'Calculate AEP' }
                    onClick={update}
                    disabled={fetchedParams ? getParams().every( (v, i) => fetchedParams[i] === v) : false}
                />
            </Box>
            { fetchedParams &&
                <Section title='Results'>
                    <Box direction={responsiveDir} gap='small' align='start' justify='between'>
                        {
                            aep === undefined || 'baseAep' in aep
                            ? <Box direction='row' gap='small'>
                                <ValueResult label='Baseline AEP' hint='The baseline Annual Energy Production of the turbine without control changes' value={aep ? (formatEnergy(aep.baseAep)) : undefined} />
                                <ValueResult label='AEP' hint='The calculated Annual Energy Production with control changes' value={aep ? (aep.newAep ? formatEnergy(aep.newAep) : '-') : undefined} />
                                <ValueResult label='Relative Change' hint='Percentage AEP change due to control changes' value={aep ? (aep.relativeChange ? `${round(100 * aep.relativeChange, 2)} %` : '-') : undefined} />
                            </Box>
                            : <Error />
                        }
                        <ChartContainer>
                            { torqueSpeedChart !== undefined
                                ? (
                                    'baseLinePlot' in torqueSpeedChart
                                    ? <TorqueSpeedChart data={torqueSpeedChart} />
                                    : <ChartError />
                                )
                                : <ChartLoader />
                            }
                        </ChartContainer>
                    </Box>
                </Section>
            }
            <Footer align='center' justify='center' direction='column' pad='medium'>
                <Anchor href='https://bitbloom.tech/' target='_blank'>
                    <Image src={darkTheme ? '/Bitbloom-White.svg' : '/Bitbloom-Black.svg'} height='42px' />
                </Anchor>
                <Box align='center' gap='medium'>
                    <Text textAlign='center'>Find a bug or need help?<br/>Contact our support team <Anchor href='mailto:support@bitbloom.tech'>support@bitbloom.tech</Anchor></Text>
                    <Text textAlign='center'>Want to know more?<br/>Say hello <Anchor href='mailto:hello@bitbloom.tech'>hello@bitbloom.tech</Anchor></Text>
                </Box>
                <Text>&copy; {new Date().getFullYear()} Bitbloom, Inc. All rights reserved.</Text>
            </Footer>
        </Box>
    </Main>
}
